import * as React from "react";
import { DrawdownIncomeAmendmentModel } from "../../../../../../../Wrap/Models/Models/Processes/Drawdown/DrawdownIncomeAmendmentModel";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { boolParse, dateParser, prepareTypography } from "../../../Common/Review/ReviewParser";
import { Urls } from "../../../../../Urls";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { FrequencyArraysMap } from "../../../Common/Resource/Contents";
import { editIncomePaymentDetailsLabels } from "../../Resource/Contents";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { AssetsToSellTable } from "./AssetsToSellTable";
import { PensionContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContributionModel";
import { frequencyMap } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { incomePaymentDetailsLabels } from "../../../Common/SIPP/IncomePaymentDetails/Resource/Content";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";


interface IReviewProps extends IProcessComponentProps {
    data: PensionContributionModel;
    customerId?: number;
    extraInfo: {
        AllBankAccounts: Array<BankAccount>;
        Drawdowntype: DrawdownType;
    };
}

interface IReviewState {}

const incomePaymentConfig = (annualIncome: DrawdownAnnualIncome, isBefore: boolean) => {
    const incomePaymentConfig = [
        {
            title: incomePaymentDetailsLabels.needToProvideAnIncome,
            data: boolParse(annualIncome?.TaxableIncomeRequiredInAddition),
        },
    ];

    if (!annualIncome?.TaxableIncomeRequiredInAddition) {
        return incomePaymentConfig;
    }

    incomePaymentConfig.push({
        title: editIncomePaymentDetailsLabels.targetSpecificArrangements,
        data: boolParse(annualIncome?.TargetSpecificArrangements),
    });

    if (annualIncome?.TargetSpecificArrangements === false) {
        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.takeDifferentInitialIncomePayment,
            data: boolParse(annualIncome?.TakeDifferentInitialIncomePayment),
        });
        if (annualIncome?.TakeDifferentInitialIncomePayment) {
            incomePaymentConfig.push({
                title: editIncomePaymentDetailsLabels.grossInitialIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat(annualIncome?.GrossInitialIncomePayment),
            });
        }

        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.grossIncomePerPayment,
            data: MoneyDisplayHelper.FromatMoneyDisplay(isBefore ? getPerPayment(annualIncome?.AnnualAmount?.Amount?.Amount, annualIncome?.Frequency) : annualIncome?.GrossIncomePerPayment?.Amount, CurrencyCode.GBP, true, 2),
        });
    } else {
        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.grossIncomePerPaymentFromPension,
            data: MoneyDisplayHelper.FromatMoneyDisplay(isBefore ? getPerPayment(annualIncome?.AnnualAmount?.Amount?.Amount, annualIncome?.Frequency) : annualIncome?.GrossIncomePerPaymentFromPension?.Amount, CurrencyCode.GBP, true, 2),
        });
    }

    incomePaymentConfig.push(
        {
            title: editIncomePaymentDetailsLabels.incomeFrequency,
            data: FrequencyArraysMap[annualIncome?.Frequency],
        },
        {
            title: editIncomePaymentDetailsLabels.firstPaymentDate,
            data: annualIncome?.NextPayment ? dateParser(annualIncome?.NextPayment) : null,
        },
        {
            title: "End date",
            data: annualIncome?.PaymentEnd == null ? null : dateParser(annualIncome?.PaymentEnd),
        }
    );

    return incomePaymentConfig;
};

const bankAccountDetailsConfig = (incomePaymentAllocations, contribution) => {
    if (!incomePaymentAllocations) {
        return [];
    }

    const bankAccountOptions = generateBankAccountOptions(
        contribution.AllBankAccounts ?? [],
        contribution.BankAccountInstructions ?? []
    );

    const bankAccountDetails = [];

    incomePaymentAllocations.forEach((bankAccount, index) => {
        const accountId = Number(bankAccount.BankAccountId); 
        const data = bankAccountOptions.get(accountId);

        bankAccountDetails.push(
            {
                title: `Bank account ${index + 1}`,
                data, 
            },
            {
                title: "Portion of income",
                data:
                    bankAccount?.Allocation === 0 && incomePaymentAllocations.length === 1
                        ? 100
                        : bankAccount?.Allocation,
            }
        );
    });

    return bankAccountDetails;
};

const generateBankAccountOptions = (
    allBankAccounts: any[] = [],
    bankAccountInstructions: any[] = []
): Map<number, string> => {
    const options = new Map<number, string>();
    options.set(-1, incomePaymentDetailsLabels.wrapCash); // wrapCash
    if (!isNullOrEmpty(allBankAccounts) ) {
            allBankAccounts.forEach((account) => {
                if (account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint) {
                    options.set(
                        account.BankAccountId,
                        `${account?.SortCode || ""} | ${account?.AccountNumber || ""} | ${account.HolderName || ""}`
                    );
                }
            });
        }

        //by requirement - new added bank account is allowed in Edit income wizard.
        if (!isNullOrEmpty(bankAccountInstructions)) {
            bankAccountInstructions.forEach((account) => {
                var code : string = `${account.BankCodes?.BankCode || ""}${account.BankCodes?.BranchCode || ""}${account.BankCodes?.ExtraCode || ""} | ${account.AccountNumber || ""} | ${account.AccountName || ""}`;
                var isExist : boolean = false;

                options.forEach((value, key, map) => {
                    //replace whitespace with empty
                    if (value.toLowerCase().replace(/\s/g, "") == code.toLowerCase().replace(/\s/g, "")) {
                        isExist = true;
                        return;
                    }
                })

                if ((account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint) && !isExist) {
                    options.set(
                        account.Id,
                        code
                    );
                }
            });
        }

    return options;
};

const getPerPayment = (amount: number, frequency: Frequency) => {
    const mapFrequency = frequencyMap[frequency] as number
    return Number((amount / mapFrequency).toFixed(2))
}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private getDrawdownIncomeAmendmentModel(): DrawdownIncomeAmendmentModel {
        return this.props.data?.Contribution?.DrawdownIncomeAmendmentModel;
    }

    public isValid(): boolean {
        return super.isValid();
    }

    public render(): JSX.Element {
        const { extraInfo } = this.props;
        const data = this.getDrawdownIncomeAmendmentModel();
        const existingDrawdownIncome = data?.ExistingDrawdownIncome;
        const formattedIncome = data?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements
            ? MoneyDisplayHelper.FromatMoneyDisplay(
                  data?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPaymentFromPension?.Amount,
                  CurrencyCode.GBP,
                  true,
                  2
              )
            : MoneyDisplayHelper.FromatMoneyDisplay(
                  data?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount,
                  CurrencyCode.GBP,
                  true,
                  2
              );
        return (
            <>
                <h2>Review</h2>
                <div className="row narrow-gutter">
                    <h2 className="col-fluid heading-underlined mt-20">Income payment details</h2>
                    <div className="col-auto">
                        <button
                            className="edit-circle-icon-button label-sr-only"
                            title="Edit"
                            onClick={() => {
                                Navigator.navigateToAndPreserveQueryParameters(
                                    Urls.get().EditIncomeWithdrawalsIncomePaymenDetails
                                );
                            }}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="text">Edit</span>
                        </button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h3>Before</h3>
                        <Typography
                            {...prepareTypography(incomePaymentConfig(existingDrawdownIncome?.AnnualIncome, true), 1)}
                        ></Typography>
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <h3>After</h3>
                        <Typography
                            {...prepareTypography(
                                incomePaymentConfig(data?.DrawdownIncomeAmendmentInstruction.Income, false),
                                1
                            )}
                        ></Typography>
                    </div>
                </div>
                {data?.DrawdownIncomeAmendmentInstruction?.IncomeDisinvestmentAllocations?.length ? (
                    <AssetsToSellTable
                        existingData={existingDrawdownIncome?.IncomeDisinvestmentAllocations || []}
                        instructionData={data.DrawdownIncomeAmendmentInstruction.IncomeDisinvestmentAllocations || []}
                        title={`Choose assets to sell to pay income (${formattedIncome})`}
                    />
                ) : (
                    <div>
                        <h4>Asset selection method</h4>
                        <span>Proportionally across all assets</span>
                    </div>
                )}
                <h2 className="mt-48">Bank details</h2>
                <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h4>Before</h4>
                        <Typography   
                        type="auto-width"
                        config={bankAccountDetailsConfig(existingDrawdownIncome?.AnnualIncome?.IncomePaymentAllocations, this.props.data)} 
                        />
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <h4>After</h4>
                        <Typography 
                        type="auto-width"
                        config={bankAccountDetailsConfig(data?.DrawdownIncomeAmendmentInstruction?.Income?.IncomePaymentAllocations, this.props.data)}
                        />
                    </div>
                </div>       
            </>
        );
    }
}